import { useState, useCallback, useEffect } from 'react';
import { Room, LocalParticipant, ConnectionQuality } from 'livekit-client';
import { simpleLiveStudioApi } from '../services/simpleLivestudioApiService';
import { livekitEgressService } from '../services/livekitEgressService';
import { LiveStreamState } from '../types';
import { LiveEvent } from '../../Events/EventsDTO';
import { adaptiveQualityManager, NetworkCondition } from '../utils/videoQualityUtils';
import {
  STREAM_START_DELAY,
  VIDEO_TRACK_WAIT_DELAY,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '../constants';

interface UseLiveStreamingProps {
  profile: { token: string; code?: string };
  room: Room | null;
  localParticipant: LocalParticipant | null;
  roomId: string;
  liveEvent: LiveEvent | null;
  onError: (error: string) => void;
  setConnectionQualityInfo?: (qualityInfo: any) => void;
}

export const useLiveStreaming = ({
  profile,
  room,
  localParticipant,
  roomId,
  liveEvent,
  onError,
  setConnectionQualityInfo
}: UseLiveStreamingProps) => {
  const [liveStreamState, setLiveStreamState] = useState<LiveStreamState>({
    isLiveStreaming: false,
    isStartingStream: false,
    isStoppingStream: false,
    activeEgressId: null,
    streamError: null,
  });

  // Monitor network conditions and adapt quality
  useEffect(() => {
    if (!room || !localParticipant) return;

    const updateNetworkCondition = () => {
      const connectionQuality = localParticipant.connectionQuality;
      let quality: NetworkCondition['connectionQuality'] = 'unknown';

      switch (connectionQuality) {
        case ConnectionQuality.Excellent:
          quality = 'excellent';
          break;
        case ConnectionQuality.Good:
          quality = 'good';
          break;
        case ConnectionQuality.Poor:
        case ConnectionQuality.Lost:
          quality = 'poor';
          break;
        default:
          quality = 'unknown';
      }

      adaptiveQualityManager.updateNetworkCondition({ connectionQuality: quality });

      // Update context with current quality info
      if (setConnectionQualityInfo) {
        const qualityInfo = {
          level: quality,
          quality: adaptiveQualityManager.getCurrentQuality(),
          deviceCapabilities: adaptiveQualityManager.getDeviceCapabilities(),
        };
        setConnectionQualityInfo(qualityInfo);
      }
    };

    // Initial update
    updateNetworkCondition();

    // Listen for connection quality changes
    const handleConnectionQualityChanged = () => {
      updateNetworkCondition();
    };

    localParticipant.on('connectionQualityChanged', handleConnectionQualityChanged);

    return () => {
      localParticipant.off('connectionQualityChanged', handleConnectionQualityChanged);
    };
  }, [room, localParticipant]);

  // Ensure video and audio tracks are active before starting egress
  const ensureTracksActive = useCallback(async (): Promise<void> => {
    if (!room || !localParticipant) {
      throw new Error(ERROR_MESSAGES.ROOM_NOT_CONNECTED);
    }

    console.log('Checking video track status before starting egress...');
    const videoPublications = Array.from(localParticipant.videoTrackPublications.values());
    const audioPublications = Array.from(localParticipant.audioTrackPublications.values());

    console.log('Video publications count:', videoPublications.length);
    console.log('Audio publications count:', audioPublications.length);

    if (videoPublications.length === 0) {
      console.log('No video tracks found, enabling camera...');
      await localParticipant.enableCameraAndMicrophone();
      // Wait for video track to be published
      await new Promise(resolve => setTimeout(resolve, VIDEO_TRACK_WAIT_DELAY));
    } else {
      console.log('Video track found, ensuring it\'s enabled...');
      const videoPublication = videoPublications[0];
      if (videoPublication && !videoPublication.isEnabled) {
        await localParticipant.enableCameraAndMicrophone();
      }
    }

    // Wait for room to be stable
    await new Promise(resolve => setTimeout(resolve, STREAM_START_DELAY));

    // Final check: ensure we have active video and audio tracks
    const finalVideoPublications = Array.from(localParticipant.videoTrackPublications.values());
    const finalAudioPublications = Array.from(localParticipant.audioTrackPublications.values());

    console.log('Final track check before egress:');
    console.log('- Video publications:', finalVideoPublications.length);
    console.log('- Audio publications:', finalAudioPublications.length);

    if (finalVideoPublications.length === 0) {
      throw new Error(ERROR_MESSAGES.NO_VIDEO_TRACK);
    }
    if (finalAudioPublications.length === 0) {
      throw new Error(ERROR_MESSAGES.NO_AUDIO_TRACK);
    }

    // Check if tracks are enabled
    const videoTrack = finalVideoPublications[0];
    const audioTrack = finalAudioPublications[0];

    console.log('Track status:');
    console.log('- Video enabled:', videoTrack?.isEnabled);
    console.log('- Audio enabled:', audioTrack?.isEnabled);

    if (!videoTrack?.isEnabled) {
      throw new Error(ERROR_MESSAGES.VIDEO_TRACK_DISABLED);
    }
    if (!audioTrack?.isEnabled) {
      throw new Error(ERROR_MESSAGES.AUDIO_TRACK_DISABLED);
    }
  }, [room, localParticipant]);


  // Construct RTMP URL from liveEvent data
  const getRtmpUrl = useCallback((): string => {
    if (!liveEvent || !liveEvent.event_transmission_accesses || liveEvent.event_transmission_accesses.length === 0) {
      throw new Error('No transmission access data available for live event');
    }

    const transmissionAccess = liveEvent.event_transmission_accesses[0];
    const rtmpUrl = `${transmissionAccess.transmission_url}/${transmissionAccess.reference}`;

    console.log('Constructed RTMP URL:', rtmpUrl);
    return rtmpUrl;
  }, [liveEvent]);

  // Start live stream
  const startLiveStream = useCallback(async (): Promise<void> => {
    if (!room || !roomId) {
      const error = ERROR_MESSAGES.ROOM_NOT_CONNECTED;
      setLiveStreamState(prev => ({ ...prev, streamError: error }));
      onError(error);
      return;
    }

    setLiveStreamState(prev => ({
      ...prev,
      isStartingStream: true,
      streamError: null
    }));

    try {
      // Ensure tracks are active
      await ensureTracksActive();

      // Get RTMP URL from liveEvent data
      const rtmpUrl = getRtmpUrl();

      // Get optimal quality based on network conditions and device capabilities
      const currentQuality = adaptiveQualityManager.getCurrentQuality();
      console.log('Starting stream with quality:', currentQuality);

      // Start primary RTMP stream to Wowza using profile token directly
      const response = await livekitEgressService.startRtmpStream(
        profile.token,
        roomId,
        rtmpUrl,
        undefined, // streamKey
        currentQuality
      );


      setLiveStreamState(prev => ({
        ...prev,
        activeEgressId: response.egress_id,
        isLiveStreaming: true,
        isStartingStream: false,
      }));

      console.log(SUCCESS_MESSAGES.STREAM_STARTED, response);
    } catch (error: any) {
      console.error('Failed to start live stream:', error);
      const errorMessage = error.message || ERROR_MESSAGES.FAILED_TO_START_STREAM;
      setLiveStreamState(prev => ({
        ...prev,
        streamError: errorMessage,
        isStartingStream: false
      }));
      onError(errorMessage);
    }
  }, [room, roomId, profile.token, liveEvent, ensureTracksActive, getRtmpUrl, onError]);

  // Stop live stream
  const stopLiveStream = useCallback(async (): Promise<void> => {
    if (!liveStreamState.activeEgressId) {
      const error = ERROR_MESSAGES.NO_ACTIVE_STREAM;
      setLiveStreamState(prev => ({ ...prev, streamError: error }));
      onError(error);
      return;
    }

    setLiveStreamState(prev => ({
      ...prev,
      isStoppingStream: true,
      streamError: null
    }));

    try {
      // Stop RTMP stream using profile token directly
      await livekitEgressService.stopRtmpStream(profile.token, liveStreamState.activeEgressId);


      setLiveStreamState(prev => ({
        ...prev,
        activeEgressId: null,
        isLiveStreaming: false,
        isStoppingStream: false,
      }));

      console.log(SUCCESS_MESSAGES.STREAM_STOPPED);
    } catch (error: any) {
      console.error('Failed to stop live stream:', error);
      const errorMessage = error.message || ERROR_MESSAGES.FAILED_TO_STOP_STREAM;
      setLiveStreamState(prev => ({
        ...prev,
        streamError: errorMessage,
        isStoppingStream: false
      }));
      onError(errorMessage);
    }
  }, [liveStreamState.activeEgressId, profile.token, profile.code, onError]);

  // Clear stream error
  const clearStreamError = useCallback(() => {
    setLiveStreamState(prev => ({ ...prev, streamError: null }));
  }, []);

  // Get current quality information
  const getCurrentQuality = useCallback(() => {
    return {
      level: adaptiveQualityManager.getCurrentQuality(),
      preset: adaptiveQualityManager.getCurrentPreset(),
      deviceCapabilities: adaptiveQualityManager.getDeviceCapabilities(),
    };
  }, []);

  // Set quality manually (for user override)
  const setQuality = useCallback((quality: 'HIGH' | 'MEDIUM' | 'LOW') => {
    adaptiveQualityManager.setQuality(quality);
  }, []);

  return {
    liveStreamState,
    startLiveStream,
    stopLiveStream,
    clearStreamError,
    getCurrentQuality,
    setQuality,
  };
};
